/**
 * Marketing landing page at `/` (#103, FR-15.1; repositioned for Jeopardy relevance in
 * #353, FR-15.7). The app's front door: it explains what the product is and drives the
 * visitor into a game. A split hero (H1 + CTAs left, live board visual right) → semantic
 * sections that establish the *create → multiplayer → customize → how-it-works* graph and
 * cross-link the four product-intent landing pages → a short FAQ (incl. the non-affiliation
 * clarification, D353.1) + repeat CTA + the trademark disclaimer footnote. The site-wide
 * {@link ../ui/Footer} is rendered by the App layout below every route, so it is
 * intentionally NOT rendered here.
 *
 * The H1 is *"Create and Play Custom Jeopardy-Style Games Online"* (#353): the old
 * *"Trivia night, reinvented"* phrase steps down to the hero eyebrow (brand copy), no
 * longer the H1. "Jeopardy-style" is descriptive nominative use permitted by FR-9.11
 * (D353.1); the product name stays {@link BRAND_NAME}.
 *
 * All brand mentions read from {@link BRAND_NAME} / the round-name tokens (FR-9.11) — no
 * hard-coded "Peril". The page is **SSR-safe** (no `window`/`localStorage`/timers at
 * module or render top-level) so the build-time prerender ({@link ../prerender-entry})
 * can bake it into the served HTML for crawlers (FR-15.2). Its per-route `<head>` (title,
 * description, canonical, OG/Twitter + share image, JSON-LD) is applied by
 * {@link useDocumentHead}; the same {@link LANDING_SEO} model is baked at prerender time.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadIdentity } from '../net/auth';
import { BRAND_NAME, DOUBLE_PERIL, FINAL_PERIL, HIDDEN_PERIL } from '../ui/brand';
import { LandingHeader } from '../ui/landing/LandingHeader';
import { BoardVisual } from '../ui/landing/BoardVisual';
import { IosBadge } from '../ui/landing/IosBadge';
import { useDocumentHead } from '../seo/useDocumentHead';
import { LANDING_SEO } from '../seo/landing-seo';
import { AFFILIATION_ANSWER, SEO_DISCLAIMER } from '../seo/trademark';

/**
 * The zero-commitment CTA group (#246, D246.10 — CTA Option A; relabeled for the #353
 * Jeopardy-relevance repositioning). The amber **primary** action for cold traffic is
 * "Create a Game →" (→ the curated topic picker at `/demo`); **Host** and **Join** step
 * down to ghost buttons, and a reassurance note spells out the no-commitment promise. The
 * primary still targets `/demo` (the zero-commitment picker) — only the label changed, to
 * lead with the create-a-game intent the SEO copy targets (D353.7 keeps it above the fold).
 * Reused in the hero and the closing section; each instance gets distinct testIds so
 * navigation assertions resolve a single element (`link-host` stays the hero CTA the in-app
 * home affordance expects).
 */
function DemoCtaGroup({
  demoTestId,
  hostTestId,
  joinTestId,
  registered,
}: {
  demoTestId: string;
  hostTestId: string;
  joinTestId?: string;
  /**
   * True for a signed-in (registered) visitor. They already have an account, so the
   * zero-commitment demo CTA + its "no account" note are dropped — Host becomes the
   * primary CTA (with Join alongside), the pre-#246 signed-in front door.
   */
  registered: boolean;
}): React.JSX.Element {
  if (registered) {
    return (
      <div className="landing-cta-row">
        <Link
          className="btn btn--primary landing-cta"
          to="/host"
          data-testid={hostTestId}
        >
          Host a game
        </Link>
        {joinTestId && (
          <Link
            className="btn btn--ghost landing-cta"
            to="/player"
            data-testid={joinTestId}
          >
            Join a Game
          </Link>
        )}
      </div>
    );
  }
  return (
    <>
      <div className="landing-cta-row landing-cta-row--stack">
        <Link
          className="btn btn--primary landing-cta landing-cta--demo"
          to="/demo"
          data-testid={demoTestId}
        >
          Create a Game →
        </Link>
        <Link className="btn btn--ghost landing-cta" to="/host" data-testid={hostTestId}>
          Host a game
        </Link>
        {joinTestId && (
          <Link
            className="btn btn--ghost landing-cta"
            to="/player"
            data-testid={joinTestId}
          >
            Join a Game
          </Link>
        )}
      </div>
      <p className="landing-demo-note">
        Pick 3 topics — no account, no download, instant board
      </p>
    </>
  );
}

/**
 * Whether the visitor is a signed-in registered account. Defaults to `false` for the
 * first render so SSR/prerender (which has no session) and client hydration agree; a
 * post-mount effect reads the persisted identity and flips it (a fresh sign-in does a
 * full-document navigation, so this re-reads on every load — no stale CTA).
 */
function useRegistered(): boolean {
  const [registered, setRegistered] = useState(false);
  useEffect(() => {
    const id = loadIdentity();
    setRegistered(id !== null && !id.isGuest);
  }, []);
  return registered;
}

export function Landing(): React.JSX.Element {
  useDocumentHead(LANDING_SEO);
  const registered = useRegistered();

  return (
    <div className="landing" data-testid="landing">
      <LandingHeader />

      <header className="landing-wrap landing-hero">
        <div className="landing-hero__copy">
          <span className="landing-eyebrow">Trivia night, reinvented</span>
          <h1 className="landing-hero__title">
            Create and Play Custom <span className="landing-amber">Jeopardy-Style</span>{' '}
            Games Online
          </h1>
          <p className="landing-hero__lead">
            Generate a complete trivia board around any topics you choose. Host the game
            from your browser while players join and buzz in from their phones.
          </p>
          <DemoCtaGroup
            demoTestId="link-demo"
            hostTestId="link-host"
            joinTestId="link-player"
            registered={registered}
          />
          <p className="landing-subnote">
            Free to try · No download · Play on any device
          </p>
          <div className="landing-appcta">
            <IosBadge />
          </div>
        </div>
        <BoardVisual />
      </header>

      <section className="landing-wrap landing-section" data-testid="landing-create">
        <div className="landing-sec-head">
          <span className="landing-eyebrow">Create</span>
          <h2>Create a Game Around Any Topic</h2>
          <p>
            Pick any topics and {BRAND_NAME}&apos;s{' '}
            <Link to="/jeopardy-game-maker">Jeopardy game maker</Link> generates a full
            board of categories and clues on demand — no stale question packs, no two
            nights the same. Prefer to just{' '}
            <Link to="/online-jeopardy">play a Jeopardy-style game online</Link>? Jump
            straight in.
          </p>
        </div>
      </section>

      <section className="landing-wrap landing-section" data-testid="landing-multiplayer">
        <div className="landing-sec-head">
          <span className="landing-eyebrow">Play together</span>
          <h2>A Live Multiplayer Buzzer</h2>
          <p>
            Host from your browser and cast the board to the TV. Players join{' '}
            <Link to="/multiplayer-jeopardy">multiplayer Jeopardy-style games</Link> from
            their phones — no app download — and race a real-time buzzer. First buzz wins.
          </p>
        </div>
      </section>

      <section className="landing-wrap landing-section" data-testid="landing-customize">
        <div className="landing-sec-head">
          <span className="landing-eyebrow">Customize</span>
          <h2>Customize Every Game</h2>
          <p>
            Every board is yours: choose the topics, review the generated categories and
            clues, and tweak anything before you start. Build{' '}
            <Link to="/custom-jeopardy-games">custom Jeopardy-style games</Link> around
            the things your group actually cares about.
          </p>
        </div>
      </section>

      <section
        id="how"
        className="landing-wrap landing-section"
        data-testid="landing-how"
      >
        <div className="landing-sec-head">
          <span className="landing-eyebrow">How {BRAND_NAME} works</span>
          <h2>How {BRAND_NAME} Works</h2>
        </div>
        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step__n">1</div>
            <h3>Pick your topics</h3>
            <p>
              Choose what your group loves — anything from &apos;90s movies to biology.
            </p>
          </div>
          <div className="landing-step">
            <div className="landing-step__n">2</div>
            <h3>Generate your game</h3>
            <p>{BRAND_NAME} writes a full Jeopardy-style board of clues on demand.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step__n">3</div>
            <h3>Share the link</h3>
            <p>Players join from their phones by code or QR — no app to install.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step__n">4</div>
            <h3>Buzz &amp; play</h3>
            <p>
              Race the buzzer through {HIDDEN_PERIL}, {DOUBLE_PERIL}, and {FINAL_PERIL}.
            </p>
          </div>
        </div>
      </section>

      <section className="landing-wrap landing-section" data-testid="landing-usecases">
        <div className="landing-sec-head">
          <span className="landing-eyebrow">Anywhere</span>
          <h2>Built for Game Nights, Classrooms, Parties and Teams</h2>
          <p>
            Because everyone joins by link and buzzes in from their own device,{' '}
            {BRAND_NAME} works for a family game night on the couch, a classroom review, a
            party icebreaker, or a remote team spread across time zones.
          </p>
        </div>
      </section>

      <section
        className="landing-wrap landing-section landing-why"
        data-testid="landing-faq"
      >
        <div className="landing-sec-head">
          <span className="landing-eyebrow">FAQ</span>
          <h2>Frequently Asked Questions</h2>
          <p>
            <strong>Is {BRAND_NAME} affiliated with Jeopardy?</strong>
            <br />
            {AFFILIATION_ANSWER}
          </p>
          <p>
            <Link to="/faq">Read more answers →</Link>
          </p>
        </div>
        <DemoCtaGroup
          demoTestId="link-demo-bottom"
          hostTestId="link-host-bottom"
          registered={registered}
        />
        <p className="seo-disclaimer">{SEO_DISCLAIMER}</p>
      </section>
    </div>
  );
}
